import type { ParentSession } from "@spork/s4p-server-sdk";
import dayjs from "dayjs";
import "dayjs/locale/cs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Platform, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { toastError } from "@/components/AppToast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { ListSection } from "@/components/ui/list-section";
import { Text } from "@/components/ui/text";
import { useAuth } from "@/lib/auth";
import { showAlert } from "@/lib/showAlert";
import {
    useGetParentSessionsQuery,
    useRevokeAllParentSessionsMutation,
    useRevokeParentSessionMutation,
} from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";
import type { FontAwesome6ProRegularIconName } from "@react-native-vector-icons/fontawesome6-pro";
import type { TFunction } from "i18next";
import { cn } from "../../../lib/utils";

dayjs.extend(relativeTime);

function isWebSession(session: ParentSession): boolean {
    const type = session.device_info?.type?.toLowerCase();
    return (
        type === "web" ||
        (!session.is_native_device && Boolean(session.device_info?.browser))
    );
}

function getSessionIcon(
    session: ParentSession,
): FontAwesome6ProRegularIconName {
    return isWebSession(session) ? "display" : "mobile";
}

function getSessionTitle(session: ParentSession, t: TFunction): string {
    if (isWebSession(session)) {
        return (
            session.device_info?.browser?.trim() ||
            t("activeSessions.device.webBrowser")
        );
    }

    const deviceName = session.device_info?.device_name?.trim();
    if (deviceName) {
        return deviceName;
    }

    const type = session.device_info?.type?.toLowerCase();
    if (type === "ios" || type === "iphone") {
        return t("activeSessions.device.iphone");
    }
    if (type === "android") {
        return t("activeSessions.device.android");
    }
    if (session.is_native_device) {
        return t("activeSessions.device.mobileApp");
    }
    return (
        session.device_info?.browser?.trim() ||
        t("activeSessions.device.unknown")
    );
}

function formatLastActiveAt(
    iso: string,
    t: TFunction,
    language: string,
): string {
    const locale = language.startsWith("cs") ? "cs" : "en";
    const date = dayjs(iso).locale(locale);
    if (!date.isValid()) {
        return t("activeSessions.lastActiveRecently");
    }
    return t("activeSessions.lastActive", { relative: date.fromNow() });
}

function getSessionLocation(session: ParentSession): string | null {
    const parts: string[] = [];
    if (session.device_info?.city?.trim()) {
        parts.push(session.device_info.city.trim());
    }
    if (session.device_info?.ip_address?.trim()) {
        parts.push(session.device_info.ip_address.trim());
    }
    return parts.length > 0 ? parts.join(" · ") : null;
}

function SessionRow({
    session,
    disabled,
    onSignOut,
}: {
    session: ParentSession;
    disabled: boolean;
    onSignOut: (session: ParentSession) => void;
}) {
    const { t, i18n } = useTranslation();
    const location = getSessionLocation(session);

    return (
        <View className="flex-row items-center justify-between gap-3 px-4 py-3">
            <View className="min-w-0 flex-1 flex-row items-center gap-3">
                <View className="w-6 items-center">
                    <Icon
                        name={getSessionIcon(session)}
                        iconStyle="regular"
                        size={20}
                        className="text-primary"
                    />
                </View>
                <View className="min-w-0 flex-1 gap-0.5">
                    <View className="flex-row flex-wrap items-center gap-2">
                        <Text variant="bold" className="shrink">
                            {getSessionTitle(session, t)}
                        </Text>
                        {session.is_current ? (
                            <Badge variant="secondary">
                                <Text>{t("activeSessions.thisDevice")}</Text>
                            </Badge>
                        ) : null}
                    </View>
                    {location ? <Text variant="muted">{location}</Text> : null}
                    <Text variant="muted">
                        {formatLastActiveAt(
                            session.last_used_at,
                            t,
                            i18n.resolvedLanguage ?? i18n.language,
                        )}
                    </Text>
                </View>
            </View>

            <Button
                variant="outline"
                size={Platform.select({ web: "sm", default: "xs" })}
                disabled={disabled}
                onPress={() => onSignOut(session)}
                testID={
                    session.is_current
                        ? TEST_IDS[
                              TEST_IDS.ACTIVE_SESSIONS_SIGN_OUT_CURRENT_BUTTON
                          ]
                        : TEST_IDS[
                              TEST_IDS.ACTIVE_SESSIONS_SIGN_OUT_OTHER_BUTTON
                          ]
                }
            >
                <Text
                    className={cn(
                        "text-sm",
                        disabled ? "text-muted-foreground" : "text-destructive",
                    )}
                >
                    {t("activeSessions.signOut")}
                </Text>
            </Button>
        </View>
    );
}

export function ActiveSessionsScreen() {
    const { t } = useTranslation();
    const { signOut } = useAuth();
    const {
        data: sessions,
        isLoading,
        isError,
        refetch,
        isFetching,
    } = useGetParentSessionsQuery();
    const [revokeSession, { isLoading: isRevokingOne }] =
        useRevokeParentSessionMutation();
    const [revokeAll, { isLoading: isRevokingAll }] =
        useRevokeAllParentSessionsMutation();

    const busy = isRevokingOne || isRevokingAll;

    const handleRevoke = useCallback(
        (session: ParentSession) => {
            const title = session.is_current
                ? t("activeSessions.revokeCurrentTitle")
                : t("activeSessions.revokeOtherTitle");
            const message = session.is_current
                ? t("activeSessions.revokeCurrentMessage")
                : t("activeSessions.revokeOtherMessage", {
                      device: getSessionTitle(session, t),
                  });

            showAlert(title, message, [
                { text: t("ui.cancel"), variant: "outline" },
                {
                    text: t("activeSessions.signOut"),
                    variant: "destructive",
                    testID: TEST_IDS[
                        TEST_IDS.ACTIVE_SESSIONS_CONFIRM_SIGN_OUT_BUTTON
                    ],
                    onPress: async () => {
                        try {
                            await revokeSession(session.id).unwrap();
                            if (session.is_current) {
                                await signOut();
                            }
                        } catch (error) {
                            toastError(
                                t("activeSessions.revokeFailedTitle"),
                                error instanceof Error
                                    ? error.message
                                    : t("activeSessions.revokeFailedFallback"),
                            );
                        }
                    },
                },
            ]);
        },
        [revokeSession, signOut, t],
    );

    const handleRevokeAll = useCallback(() => {
        showAlert(
            t("activeSessions.revokeAllTitle"),
            t("activeSessions.revokeAllMessage"),
            [
                { text: t("ui.cancel"), variant: "outline" },
                {
                    text: t("activeSessions.signOutAll"),
                    variant: "destructive",
                    testID: TEST_IDS[
                        TEST_IDS.ACTIVE_SESSIONS_CONFIRM_SIGN_OUT_ALL_BUTTON
                    ],
                    onPress: async () => {
                        try {
                            await revokeAll().unwrap();
                            await signOut();
                        } catch (error) {
                            toastError(
                                t("activeSessions.revokeAllFailedTitle"),
                                error instanceof Error
                                    ? error.message
                                    : t("activeSessions.revokeFailedFallback"),
                            );
                        }
                    },
                },
            ],
        );
    }, [revokeAll, signOut, t]);

    return (
        <SafeAreaView className="flex-1 bg-background" edges={["bottom"]}>
            <ScrollView
                className="flex-1"
                contentContainerClassName="gap-6 px-4 pb-6 pt-2 web:mx-auto web:w-full web:max-w-lg"
            >
                <View className="gap-1">
                    <Text variant="muted">
                        {t("activeSessions.description")}
                    </Text>
                </View>

                {isLoading ? (
                    <View className="items-center py-8">
                        <ActivityIndicator />
                    </View>
                ) : null}

                {isError ? (
                    <View className="gap-3 rounded-card border border-border bg-card px-4 py-4">
                        <Text variant="muted">
                            {t("activeSessions.loadError")}
                        </Text>
                        <Button
                            variant="outline"
                            onPress={() => refetch()}
                            disabled={isFetching}
                        >
                            <Text>{t("activeSessions.tryAgain")}</Text>
                        </Button>
                    </View>
                ) : null}

                {!isLoading && !isError ? (
                    <ListSection title={t("activeSessions.sectionTitle")}>
                        {(sessions ?? []).length === 0 ? (
                            <View className="px-4 py-4">
                                <Text variant="muted">
                                    {t("activeSessions.empty")}
                                </Text>
                            </View>
                        ) : (
                            (sessions ?? []).map((session) => (
                                <View key={session.id}>
                                    <SessionRow
                                        session={session}
                                        disabled={busy}
                                        onSignOut={handleRevoke}
                                    />
                                </View>
                            ))
                        )}
                    </ListSection>
                ) : null}

                <Button
                    variant="destructive"
                    onPress={handleRevokeAll}
                    disabled={
                        busy ||
                        isLoading ||
                        isError ||
                        (sessions?.length ?? 0) === 0
                    }
                    testID={
                        TEST_IDS[TEST_IDS.ACTIVE_SESSIONS_SIGN_OUT_ALL_BUTTON]
                    }
                >
                    <Text>
                        {isRevokingAll
                            ? t("activeSessions.signingOut")
                            : t("activeSessions.signOutAll")}
                    </Text>
                </Button>
            </ScrollView>
        </SafeAreaView>
    );
}
